import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { useRef } from 'react';
import { useMemo } from 'react';

import { makeGridPopupContainer } from '../../containers/GridPopup/GridPopup';
import { Bem } from '../../service/Bem/Bem';
import { BasePopupProps, LinkTarget, ThemePopup } from '../../typings/common';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';

import './PricePackagesClientsListTooltip.css';
import * as keyset from './PricePackagesClientsListTooltip.i18n';

const i18n = i18nFactory(keyset);

const BLOCK = 'price-packages-clients-list-tooltip';

export interface PricePackagesClientsListTooltipProps {
    clientIds: string[];
    canAddClients: boolean;
    onModalShow: () => void;
}

export const PricePackagesClientsListTooltip: React.FC<
    PricePackagesClientsListTooltipProps & BasePopupProps
> = React.memo(({ openPopup, clientIds, onModalShow, canAddClients, isPopupVisible, closePopup }) => {
    const tooltipAnchor = useRef(null);
    const buttonAddClients = useMemo(() => {
        if (!canAddClients) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'action')}>
                <ButtonDecorator
                    cls={Bem.cls(BLOCK, 'button')}
                    theme={'clear'}
                    size={'n'}
                    text={i18n('add-clients')}
                    onClick={onModalShow}
                />
            </div>
        );
    }, [onModalShow, canAddClients]);

    const clientsList = useMemo(() => {
        if (!clientIds.length) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'clients-list')}>
                {clientIds.map(clientid => {
                    return (
                        <div key={clientid} className={Bem.cls(BLOCK, 'clients-item')}>
                            <StyledLink
                                cls={Bem.cls(BLOCK, 'clients-item-link')}
                                theme={StyledLinkTheme.black}
                                href={''}
                                target={LinkTarget.blank}
                                title={'clientId'}
                            >
                                {clientid}
                            </StyledLink>
                        </div>
                    );
                })}
            </div>
        );
    }, [clientIds]);

    const clientsPopup = useMemo(() => {
        if (!canAddClients && clientIds.length === 0) {
            return null;
        }

        return (
            <>
                <Shape2
                    cls={Bem.cls(BLOCK, 'icon')}
                    ref={tooltipAnchor}
                    width={12}
                    height={12}
                    hoverable={true}
                    theme={Shape2Theme.default}
                    icon={Shape2Icon.link2}
                    onClick={openPopup}
                />
                <PopupDecorator
                    cls={Bem.cls(BLOCK, 'popup')}
                    theme={ThemePopup.CLEAR}
                    target={'anchor'}
                    directions={['right-center', 'left-center', 'bottom-center', 'top-center']}
                    hasTail={false}
                    hasClose={false}
                    visible={isPopupVisible}
                    anchor={tooltipAnchor.current}
                    onOutsideClick={closePopup}
                    onClose={closePopup}
                    autoclosable={true}
                >
                    {clientsList}
                    {buttonAddClients}
                </PopupDecorator>
            </>
        );
    }, [closePopup, openPopup, canAddClients, isPopupVisible]);

    return (
        <div className={Bem.cls(BLOCK)}>
            {clientIds.length}
            {clientsPopup}
        </div>
    );
});

export const PricePackagesClientsListTooltipContainer = makeGridPopupContainer<PricePackagesClientsListTooltipProps>(
    PricePackagesClientsListTooltip
);
